/**
 * Billing Phase 2 Services
 * Extensions to billingService for late fees, discounts, reminders, and reporting
 *
 * Uses the app's shared `api` utility (not raw axios) so requests carry the
 * same Authorization bearer token as every other authenticated call in the
 * app. The previous raw-axios version never attached that header, so every
 * one of these calls 401'd against the backend's auth-required endpoints.
 */
import api from '../utils/api';

// =====================================================================
// LATE FEE OPERATIONS
// =====================================================================

export const getOverdueSubscriptions = async () => {
  try {
    const data = await api.get('/api/billing/subscriptions/overdue/list');
    return data.subscriptions || [];
  } catch (error) {
    throw { error: error.message || 'Failed to fetch overdue subscriptions' };
  }
};

export const applyLateFees = async () => {
  try {
    return await api.post('/api/billing/late-fees/apply');
  } catch (error) {
    throw { error: error.message || 'Failed to apply late fees' };
  }
};

export const waiveLateFee = async (subscriptionId, reason = 'Manual waiver') => {
  try {
    return await api.post(
      `/api/billing/subscriptions/${subscriptionId}/late-fee/waive`,
      { reason }
    );
  } catch (error) {
    throw { error: error.message || 'Failed to waive late fee' };
  }
};

// =====================================================================
// PRORATION OPERATIONS (mid-term student-count growth)
// =====================================================================

export const getProrationOverview = async () => {
  try {
    const data = await api.get('/api/billing/subscriptions/proration/list');
    return data.subscriptions || [];
  } catch (error) {
    throw { error: error.message || 'Failed to fetch proration overview' };
  }
};

export const applyProration = async () => {
  try {
    return await api.post('/api/billing/proration/apply');
  } catch (error) {
    throw { error: error.message || 'Failed to apply proration' };
  }
};

export const getProrationHistory = async (subscriptionId) => {
  try {
    const data = await api.get(`/api/billing/subscriptions/${subscriptionId}/proration-history`);
    return data.charges || [];
  } catch (error) {
    throw { error: error.message || 'Failed to fetch proration history' };
  }
};

// =====================================================================
// CONFIG (per-school pricing, used for discount-impact examples)
// =====================================================================

export const getBillingConfig = async (schoolId) => {
  try {
    return await api.get(`/api/billing/config/${schoolId}`);
  } catch (error) {
    throw { error: error.message || 'Failed to fetch billing configuration' };
  }
};

export const updateBillingConfig = async (schoolId, data) => {
  try {
    return await api.put(`/api/billing/config/${schoolId}`, data);
  } catch (error) {
    throw { error: error.message || 'Failed to update billing configuration' };
  }
};

export const updateAutoRenew = async (schoolId, autoRenewEnabled) => {
  try {
    return await api.put(`/api/billing/config/${schoolId}/auto-renew`, { auto_renew_enabled: autoRenewEnabled });
  } catch (error) {
    throw { error: error.message || 'Failed to update auto-renew setting' };
  }
};

export const updatePlanTier = async (schoolId, planTier) => {
  try {
    return await api.put(`/api/billing/config/${schoolId}/plan-tier`, { plan_tier: planTier });
  } catch (error) {
    throw { error: error.message || 'Failed to update plan tier' };
  }
};

// =====================================================================
// DISCOUNT OPERATIONS
// =====================================================================

// school_id is required by the backend (POST /billing/discounts/rules) —
// granting a discount always applies to one specific school, chosen by the
// super admin, never inferred.
export const createDiscountRule = async (
  schoolId,
  minStudents,
  discountPercentage,
  maxStudents = null,
  description = ''
) => {
  try {
    return await api.post('/api/billing/discounts/rules', {
      school_id: schoolId,
      min_students: minStudents,
      discount_percentage: discountPercentage,
      max_students: maxStudents,
      description: description || `${minStudents}+ students discount`,
    });
  } catch (error) {
    throw { error: error.message || 'Failed to create discount rule' };
  }
};

export const listDiscountRules = async (schoolId) => {
  try {
    const data = await api.get('/api/billing/discounts/rules', {
      params: { school_id: schoolId }
    });
    return data.rules || [];
  } catch (error) {
    throw { error: error.message || 'Failed to fetch discount rules' };
  }
};

export const toggleDiscountRule = async (ruleId, isActive) => {
  try {
    return await api.put(`/api/billing/discounts/rules/${ruleId}/toggle`, {
      is_active: isActive
    });
  } catch (error) {
    throw { error: error.message || 'Failed to toggle discount rule' };
  }
};

// =====================================================================
// REMINDER OPERATIONS
// =====================================================================

export const sendPendingReminders = async () => {
  try {
    return await api.post('/api/billing/reminders/send-pending');
  } catch (error) {
    throw { error: error.message || 'Failed to send reminders' };
  }
};

export const getReminderHistory = async (subscriptionId, limit = 10, offset = 0) => {
  try {
    return await api.get(
      `/api/billing/subscriptions/${subscriptionId}/reminders`,
      { params: { limit, offset } }
    );
  } catch (error) {
    throw { error: error.message || 'Failed to fetch reminder history' };
  }
};

// =====================================================================
// REPORTING OPERATIONS
// =====================================================================

export const getRevenueReport = async (schoolId = null, academicYear = null) => {
  try {
    const params = {};
    if (schoolId) params.school_id = schoolId;
    if (academicYear) params.academic_year = academicYear;

    return await api.get('/api/billing/reports/revenue', { params });
  } catch (error) {
    throw { error: error.message || 'Failed to fetch revenue report' };
  }
};

export const getAgingAnalysis = async (schoolId = null) => {
  try {
    const params = {};
    if (schoolId) params.school_id = schoolId;

    return await api.get('/api/billing/reports/aging', { params });
  } catch (error) {
    throw { error: error.message || 'Failed to fetch aging analysis' };
  }
};

export const getTopPayingSchools = async (limit = 10) => {
  try {
    const data = await api.get('/api/billing/reports/top-paying-schools', {
      params: { limit },
    });
    return data.schools || [];
  } catch (error) {
    throw { error: error.message || 'Failed to fetch top paying schools' };
  }
};

export const getBottomPayingSchools = async (limit = 10) => {
  try {
    const data = await api.get('/api/billing/reports/bottom-paying-schools', {
      params: { limit },
    });
    return data.schools || [];
  } catch (error) {
    throw { error: error.message || 'Failed to fetch bottom paying schools' };
  }
};
